import { BRAND } from "constants/brand";
/**
 * EditSlider form — modal for updating an existing homepage slider.
 *
 * Pre-populates all fields from the selected slider (passed via props). Enforces
 * the same vendor-XOR-link constraint as AddSlider.
 *
 * City and visibility (isHidden) can be changed independently of other fields.
 * Clearing the city dropdown (selecting "جميع المدن") sends cityId="" which
 * the backend interprets as null (makes the slider global again).
 */
import { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import useSliders from "hooks/useSliders";
import { fieldOf } from "utils/formField";

const EditSlider = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    const { loading, editSlider, error } = useSliders();
    const [link, setLink] = useState("");
    const [vendorId, setVendorId] = useState("");
    const [isHidden, setIsHidden] = useState(false);
    const [cityId, setCityId] = useState("");

    useEffect(() => {
        setLink(props.slider?.link || "");
        setVendorId(props.slider?.vendorId ? String(props.slider.vendorId) : "");
        setIsHidden(props.slider?.isHidden || false);
        setCityId(props.slider?.cityId ? String(props.slider.cityId) : "");
    }, [props.slider]);

    const submitHandler = async (e) => {
        e.preventDefault();
        const sliderData = new FormData();

        if (fieldOf(form.current, "image").files[0]) {
            sliderData.append(
                "image",
                fieldOf(form.current, "image").files[0],
                fieldOf(form.current, "image").files[0].name,
            );
        }
        sliderData.append("id", props.slider.id);
        sliderData.append("title", fieldOf(form.current, "title").value);

        if (vendorId) sliderData.append("vendorId", vendorId);
        if (link) sliderData.append("link", link);
        sliderData.append("isHidden", isHidden);
        if (cityId) {
            sliderData.append("cityId", cityId);
        } else {
            sliderData.append("cityId", "");
        }

        try {
            const response = await editSlider(sliderData);

            if (response.slider) {
                props.showMessage("success", "تم التعديل", "تم تعديل سلايدر بنجاح");
                props.close();
            } else {
                props.showMessage(
                    "error",
                    "هناك خطأ",
                    response.response.data.message || "حدث خطأ غير متوقع",
                );
            }
        } catch (err) {
            console.error("Error editing slider:", err);
            props.showMessage("error", "هناك خطأ", error || "حدث خطأ غير متوقع");
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>تعديل سلايدر</h5>
                            <FontAwesomeIcon icon={faClose} onClick={props.close} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            <Form.Group className="mb-3" controlId="image">
                                <Form.Label>صوره السلايدر</Form.Label>
                                <Form.Control
                                    name="image"
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.svg,.webp"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>
                                    اسم السلايدر<span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="title"
                                    type="text"
                                    placeholder="اسم السلايدر"
                                    defaultValue={props.slider?.title}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="vendor">
                                <Form.Label>المطعم</Form.Label>
                                <Form.Select
                                    value={vendorId}
                                    disabled={!!link}
                                    onChange={(e) => {
                                        setVendorId(e.target.value);
                                        setLink("");
                                    }}
                                >
                                    <option value={""}>----</option>
                                    {props.vendors?.map((vendor) => (
                                        <option key={vendor.id} value={vendor.id}>
                                            {vendor.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="link">
                                <Form.Label>رابط خارجي</Form.Label>
                                <Form.Control
                                    type="url"
                                    placeholder="https://example.com"
                                    value={link}
                                    disabled={!!vendorId}
                                    onChange={(e) => {
                                        setLink(e.target.value);
                                        setVendorId("");
                                    }}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="city">
                                <Form.Label>المدينة</Form.Label>
                                <Form.Select
                                    value={cityId}
                                    onChange={(e) => setCityId(e.target.value)}
                                >
                                    <option value={""}>جميع المدن</option>
                                    {props.cities?.map((city) => (
                                        <option key={city.id} value={city.id}>
                                            {city.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="isHidden">
                                <Form.Check
                                    type="checkbox"
                                    label="إخفاء السلايدر"
                                    checked={isHidden}
                                    onChange={(e) => setIsHidden(e.target.checked)}
                                />
                            </Form.Group>

                            <div className="buttons">
                                <button className="cancel" onClick={props.close} type="button">
                                    إلغاء
                                </button>
                                <button className="button" type="submit">
                                    حفظ سلايدر
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};
export default EditSlider;
